import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  PenLine,
  Upload,
  FileText,
  TrendingUp,
  Image,
  MapPin,
  Building2,
  X,
} from "lucide-react";

const menuItems = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  {
    id: "clients-group",
    label: "Clients",
    icon: Users,
    children: [
      { id: "clients-single", label: "Single Location", icon: MapPin },
      { id: "clients-multi", label: "Multi Location", icon: Building2 },
    ],
  },
  { id: "ad-creative", label: "Ad Creative", icon: Image },
  { id: "manual-entry", label: "Manual Entry", icon: PenLine },
  { id: "import", label: "Import Data", icon: Upload },
  { id: "reports", label: "Reports", icon: FileText },
];

export default function Sidebar({ currentView, onViewChange, clientCount, singleCount, multiCount, mobileOpen = false, onMobileClose = () => {} }) {
  const handleClick = (id) => {
    onViewChange(id);
    onMobileClose();
  };
  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40"
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-screen w-64 sidebar flex flex-col z-50 transition-transform duration-300 ease-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
        data-testid="sidebar"
      >
        {/* Mobile close button */}
        <button
          type="button"
          onClick={onMobileClose}
          className="lg:hidden absolute top-4 right-4 w-8 h-8 rounded-lg bg-slate-800/80 border border-slate-700 text-slate-300 hover:text-white flex items-center justify-center"
          data-testid="sidebar-close-btn"
          aria-label="Close menu"
        >
          <X className="w-4 h-4" />
        </button>
      {/* Logo */}
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white font-['Outfit']">
              MetricFlow
            </h1>
            <p className="text-xs text-slate-400">Agency Dashboard</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            if (item.children) {
              const isGroupActive = item.children.some((c) => currentView === c.id || currentView === "client-detail-single" || currentView === "client-detail-multi");
              return (
                <li key={item.id}>
                  <div className="px-4 pt-4 pb-1">
                    <p className={`text-xs font-semibold uppercase tracking-wider ${isGroupActive ? 'text-violet-400' : 'text-slate-500'}`}>
                      {item.label}
                    </p>
                  </div>
                  <ul className="space-y-0.5 ml-2">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const isActive = currentView === child.id || 
                        (child.id === "clients-single" && currentView === "client-detail-single") ||
                        (child.id === "clients-multi" && currentView === "client-detail-multi");
                      const count = child.id === "clients-single" ? singleCount : multiCount;
                      return (
                        <li key={child.id}>
                          <motion.button
                            whileHover={{ x: 4 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleClick(child.id)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                              isActive
                                ? "bg-violet-600/20 text-white border-r-2 border-violet-500"
                                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                            }`}
                            data-testid={`nav-${child.id}`}
                          >
                            <ChildIcon className={`w-4 h-4 ${isActive ? "text-violet-400" : ""}`} />
                            <span>{child.label}</span>
                            {count > 0 && (
                              <span className="ml-auto bg-violet-600/30 text-violet-300 text-xs px-2 py-0.5 rounded-full">
                                {count}
                              </span>
                            )}
                          </motion.button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            }

            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <li key={item.id}>
                <motion.button
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleClick(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? "bg-violet-600/20 text-white border-r-2 border-violet-500"
                      : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? "text-violet-400" : ""}`} />
                  <span>{item.label}</span>
                </motion.button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-700/50">
        <div className="bg-gradient-to-r from-violet-600/20 to-pink-600/20 rounded-xl p-4">
          <p className="text-xs text-slate-300 mb-2">
            Track metrics across
          </p>
          <div className="flex gap-2">
            <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-md">
              Facebook
            </span>
            <span className="text-xs bg-pink-500/20 text-pink-300 px-2 py-1 rounded-md">
              Instagram
            </span>
            <span className="text-xs bg-slate-500/20 text-slate-300 px-2 py-1 rounded-md">
              TikTok
            </span>
          </div>
        </div>
      </div>
    </aside>
    </>
  );
}
